import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCategories } from "@/context/CategoryContext";
import { convertAmount, formatMoney, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Upload, Trash2, Loader2, Inbox, Pencil, Paperclip, Search, ArrowRightLeft, RefreshCcw, ChevronDown, GitCompareArrows, Tags } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ExportButton } from "@/components/ExportButton";
import { EntityForm } from "@/components/entity/EntityForm";
import { EntityImporter } from "@/components/entity/EntityImporter";
import { EntityDetailDrawer } from "@/components/entity/EntityDetailDrawer";
import { AllocationDialog } from "@/components/AllocationDialog";

function statusVariant(status) {
  const s = (status || "").toLowerCase();
  if (["paid", "received"].includes(s)) return "bg-success/15 text-success border-success/30";
  if (["overdue"].includes(s)) return "bg-danger/15 text-danger border-danger/30";
  if (["submitted"].includes(s)) return "bg-info/15 text-info border-info/30";
  return "bg-warning/15 text-warning border-warning/30";
}

function monthLabel(ym) {
  const d = new Date(`${ym}-01T00:00:00`);
  return isNaN(d.getTime()) ? ym : d.toLocaleString("en", { month: "short", year: "numeric" });
}

export function EntityManager({ config }) {
  const { currency, rates } = useCurrency();
  const { cats } = useCategories();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [allocRow, setAllocRow] = useState(null);
  const [search, setSearch] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("q") || "";
    } catch {
      return "";
    }
  });
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState([]); // empty array = all months
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkCat, setBulkCat] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);

  const PAGE_SIZE = 100;

  const Icon = config.icon;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/entities/${config.apiPath}`);
      setRows(Array.isArray(data) ? data : []);
      setSelected(new Set());
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [config.apiPath]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!config.accountField) return;
    api.get("/entities/bank-accounts")
      .then(({ data }) => setAccounts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [config.accountField]);

  // Bank accounts are also needed by the Add/Edit form (account field selector).
  useEffect(() => {
    if (config.accountField) return; // already fetched above
    const needsAccounts = config.fields.some((f) => f.type === "account");
    if (!needsAccounts) return;
    api.get("/entities/bank-accounts")
      .then(({ data }) => setAccounts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [config.accountField, config.fields]);

  const display = useMemo(() => {
    const list = rows.map((r) => ({
      ...r,
      _amount: convertAmount(r[config.amountField], r.currency, currency, rates),
    }));
    if (config.sortDesc) list.sort((a, b) => b._amount - a._amount);
    return list;
  }, [rows, currency, rates, config.amountField, config.sortDesc]);

  const accMap = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);

  const accountOptions = useMemo(() => {
    if (!config.accountField) return null;
    const present = new Set(rows.map((r) => r[config.accountField]).filter(Boolean));
    return {
      opts: accounts.filter((a) => present.has(a.id)),
      hasUnassigned: rows.some((r) => !r[config.accountField]),
    };
  }, [rows, accounts, config.accountField]);

  const monthOptions = useMemo(() => {
    if (!config.monthField) return null;
    const set = new Set();
    rows.forEach((r) => {
      const ym = String(r[config.monthField] || "").slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(ym)) set.add(ym);
    });
    return [...set].sort().reverse();
  }, [rows, config.monthField]);

  const statusCounts = useMemo(() => {
    if (!config.statusField) return null;
    const m = {};
    // seed with master options so filter always lists Paid/Pending/Partial etc.
    const f = config.fields.find((x) => x.name === config.statusField);
    if (Array.isArray(f?.options)) f.options.forEach((o) => { m[o] = m[o] || 0; });
    rows.forEach((r) => {
      const k = r[config.statusField] || "—";
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [rows, config.statusField, config.fields]);

  const aging = useMemo(() => {
    if (!config.aging) return null;
    const df = config.aging.dateField;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const order = ["Net Due", "Due soon (30d)", "Overdue 1-30d", "Overdue 31-60d", "Overdue 60+d"];
    const sums = Object.fromEntries(order.map((k) => [k, 0]));
    const counts = Object.fromEntries(order.map((k) => [k, 0]));
    rows.forEach((r) => {
      if (config.statusField && String(r[config.statusField] || "").toLowerCase() === "paid") return;
      const fullAmt = convertAmount(r[config.amountField], r.currency, currency, rates);
      const paid = convertAmount(r.paid_amount || 0, r.currency, currency, rates);
      const amt = Math.max(fullAmt - paid, 0);
      if (amt <= 0) return;
      const raw = r[df] ? new Date(r[df]) : null;
      let key = "Net Due";
      if (raw && !isNaN(raw.getTime())) {
        const days = Math.floor((today - raw) / 86400000);
        if (days > 60) key = "Overdue 60+d";
        else if (days > 30) key = "Overdue 31-60d";
        else if (days > 0) key = "Overdue 1-30d";
        else if (days >= -30) key = "Due soon (30d)";
        else key = "Net Due";
      }
      sums[key] += amt;
      counts[key] += 1;
    });
    return order.map((k) => ({ label: k, amount: sums[k], count: counts[k] }));
  }, [rows, config, currency, rates]);

  const categoryOptions = useMemo(() => {
    if (!config.columns.some((c) => c.key === "category")) return null;
    const f = config.fields.find((x) => x.name === "category");
    const master = Array.isArray(f?.options) ? f.options : [];
    const dyn = f?.catType && Array.isArray(cats[f.catType]) ? cats[f.catType] : [];
    const fromData = rows.map((r) => r.category).filter(Boolean);
    return [...new Set([...master, ...dyn, ...fromData])].sort();
  }, [rows, config.columns, config.fields, cats]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return display.filter((r) => {
      if (monthFilter.length > 0 && !monthFilter.includes(String(r[config.monthField] || "").slice(0, 7))) return false;
      if (config.monthField && (dateFrom || dateTo)) {
        const raw = String(r[config.monthField] || "").slice(0, 10);
        if (raw) {
          if (dateFrom && raw < dateFrom) return false;
          if (dateTo && raw > dateTo) return false;
        } else if (dateFrom || dateTo) {
          return false;
        }
      }
      if (accountFilter !== "all") {
        const acc = r[config.accountField] || "";
        if (accountFilter === "__none__" ? acc : acc !== accountFilter) return false;
      }
      if (catFilter !== "all" && (r.category || "") !== catFilter) return false;
      if (statusFilter !== "all" && (r[config.statusField] || "") !== statusFilter) return false;
      if (!q) return true;
      return Object.values(r).some((v) => typeof v === "string" && v.toLowerCase().includes(q));
    });
  }, [display, search, catFilter, statusFilter, monthFilter, accountFilter, dateFrom, dateTo, config.statusField, config.monthField, config.accountField]);

  useEffect(() => {
    setPageIdx(0);
  }, [search, catFilter, statusFilter, monthFilter, accountFilter, dateFrom, dateTo]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(pageIdx, pageCount - 1);
  const paged = useMemo(
    () => filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [filtered, safePage]
  );

  const total = useMemo(
    () =>
      filtered.reduce((s, r) => {
        // For payables: exclude fully Paid rows and subtract paid_amount so advances/partials
        // reduce the Total Payable KPI (mirrors backend compute_dashboard logic).
        if (config.apiPath === "payables") {
          if (String(r.status || "").toLowerCase() === "paid") return s;
          const paid = convertAmount(r.paid_amount || 0, r.currency, currency, rates);
          const remaining = Math.max(r._amount - paid, 0);
          return s + remaining;
        }
        const isRefund =
          config.apiPath === "expenses" &&
          r._amount > 0 &&
          /refund|reversal|chargeback/i.test(`${r.category || ""} ${r.description || ""}`);
        return s + (isRefund ? -r._amount : r._amount);
      }, 0),
    [filtered, config.apiPath, currency, rates]
  );

  const openAdd = () => {
    setEditingRow(null);
    setAddOpen(true);
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setAddOpen(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/entities/${config.apiPath}/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));

  const toggleAll = () => {
    setSelected((prev) => {
      if (allFilteredSelected) {
        const next = new Set(prev);
        filtered.forEach((r) => next.delete(r.id));
        return next;
      }
      return new Set([...prev, ...filtered.map((r) => r.id)]);
    });
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected record${selected.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      await Promise.all([...selected].map((id) => api.delete(`/entities/${config.apiPath}/${id}`)));
      setRows((r) => r.filter((x) => !selected.has(x.id)));
      toast.success(`Deleted ${selected.size} records`);
      setSelected(new Set());
    } catch {
      toast.error("Some deletions failed — refresh and retry");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetCategory = async () => {
    if (!bulkCat) return toast.error("Pick a category first");
    setBulkBusy(true);
    try {
      await Promise.all([...selected].map((id) => api.put(`/entities/${config.apiPath}/${id}`, { category: bulkCat })));
      setRows((r) => r.map((x) => (selected.has(x.id) ? { ...x, category: bulkCat } : x)));
      toast.success(`Category set to "${bulkCat}" on ${selected.size} records`);
      setSelected(new Set());
      setBulkCat("");
    } catch {
      toast.error("Some updates failed — refresh and retry");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkCatOptions = useMemo(() => categoryOptions || [], [categoryOptions]);

  const MOVE_TARGETS = {
    income: "Income",
    expenses: "Expenses",
    transfers: "Internal Transfers",
    sweeps: "Sweep & FD",
    "fixed-assets": "Fixed Assets",
  };
  const moveTargets = MOVE_TARGETS[config.apiPath]
    ? Object.entries(MOVE_TARGETS).filter(([k]) => k !== config.apiPath)
    : [];

  const bulkMove = async (target) => {
    const label = MOVE_TARGETS[target];
    if (!window.confirm(`Move ${selected.size} record${selected.size === 1 ? "" : "s"} to "${label}"?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all([...selected].map((id) => api.post(`/entities/${config.apiPath}/${id}/move`, { target })));
      setRows((r) => r.filter((x) => !selected.has(x.id)));
      toast.success(`Moved ${selected.size} record${selected.size === 1 ? "" : "s"} to ${label}`);
      setSelected(new Set());
    } catch {
      toast.error("Some moves failed — refresh and retry");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const moveOne = async (id, target) => {
    const label = MOVE_TARGETS[target];
    if (!window.confirm(`Move this record to "${label}"?`)) return;
    try {
      await api.post(`/entities/${config.apiPath}/${id}/move`, { target });
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success(`Moved to ${label}`);
    } catch {
      toast.error("Move failed — refresh and retry");
    }
  };

  const renderCell = (r, col, idx) => {
    if (col.type === "rank") return <span className="font-mono text-muted-foreground">{idx + 1}</span>;
    const val = r[col.key];
    if (col.type === "money") {
      const conv = convertAmount(val, r.currency, currency, rates);
      return <span className="font-mono tabular-nums">{formatMoney(conv, currency)}</span>;
    }
    if (col.type === "date") return <span className="text-muted-foreground">{formatDate(val)}</span>;
    if (col.type === "percent") return <span className="font-mono">{val != null ? `${val}%` : "—"}</span>;
    if (col.type === "status") {
      // Vendors Payable UX: display "Pending" as "Payable" (per user request); underlying value stays "Pending"
      const displayVal = config.apiPath === "payables" && val === "Pending" ? "Payable" : val;
      return (
        <Badge variant="outline" className={`font-medium ${statusVariant(val)}`}>
          {displayVal || "—"}
        </Badge>
      );
    }
    if (col.type === "badge")
      return val ? (
        <Badge variant="secondary" className="font-normal bg-secondary text-secondary-foreground">
          {val}
        </Badge>
      ) : (
        "—"
      );
    if (col.type === "account") {
      const a = accMap[val];
      return a ? (
        <Badge variant="secondary" className="font-normal bg-secondary text-secondary-foreground">
          {a.name}
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    }
    if (col.type === "attachments") {
      const arr = Array.isArray(val) ? val : [];
      if (!arr.length) return <span className="text-muted-foreground">—</span>;
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={`${arr.length} file(s)`}>
          <Paperclip className="h-3.5 w-3.5" />
          {arr.length}
        </span>
      );
    }
    if (col.type === "direction") {
      const v = String(val || "").toLowerCase();
      if (v === "credit" || v === "cr")
        return (
          <Badge variant="outline" className="font-medium border-success/40 text-success bg-success/10">CR</Badge>
        );
      if (v === "debit" || v === "dr")
        return (
          <Badge variant="outline" className="font-medium border-danger/40 text-danger bg-danger/10">DR</Badge>
        );
      return <span className="text-muted-foreground">—</span>;
    }
    if (col.type === "user") {
      if (!val) return <span className="text-muted-foreground text-xs">—</span>;
      const label = String(val);
      const short = label.includes("@") ? label.split("@")[0] : label;
      return (
        <span className="text-xs text-muted-foreground truncate max-w-[140px] block" title={label}>
          {short}
        </span>
      );
    }
    if (col.type === "alloc") {
      const arr = Array.isArray(val) ? val.filter((a) => (a?.pct || 0) > 0) : [];
      if (!arr.length) return <span className="text-muted-foreground text-xs">— Untagged</span>;
      if (arr.length === 1)
        return (
          <Badge variant="outline" className="font-normal border-brand/30 text-brand bg-brand/5 max-w-[160px] truncate">
            {arr[0].tag_name}
          </Badge>
        );
      return (
        <Badge variant="outline" className="font-normal border-brand/30 text-brand bg-brand/5" title={arr.map((a) => `${a.tag_name} ${a.pct}%`).join(", ")}>
          {arr.length} tags
        </Badge>
      );
    }
    return <span>{val ?? "—"}</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="h-11 w-11 rounded-md bg-brand/10 border border-brand/25 flex items-center justify-center mt-0.5">
              <Icon className="h-5 w-5 text-brand" />
            </div>
          )}
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-foreground">
              {config.title}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{config.subtitle}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {config.apiPath === "payables" && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await api.post("/payables/reconcile");
                  toast.success(`Reconciled ${data.reconciled} invoice group${data.reconciled === 1 ? "" : "s"}`);
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Reconcile failed");
                }
              }}
              data-testid="payables-reconcile-all-btn"
              className="gap-2"
              title="Recompute Paid amounts from matching Expenses (by vendor + invoice_no)"
            >
              <RefreshCcw className="h-4 w-4" /> Reconcile All
            </Button>
          )}
          {(config.apiPath === "transfers" || config.apiPath === "sweeps") && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await api.post("/transfers/backfill-direction");
                  toast.success(`Tagged ${data.transfers} transfer${data.transfers === 1 ? "" : "s"} · ${data.sweeps} sweep${data.sweeps === 1 ? "" : "s"} with CR/DR`);
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Backfill failed");
                }
              }}
              data-testid={`${config.apiPath}-backfill-direction-btn`}
              className="gap-2"
              title="Infer CR/DR direction from description for existing rows"
            >
              <RefreshCcw className="h-4 w-4" /> Tag CR/DR
            </Button>
          )}
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid={`${config.apiPath}-import-btn`} className="gap-2">
            <Upload className="h-4 w-4" /> Import
          </Button>
          <ExportButton apiPath={config.apiPath} title={config.title} />
          <Button onClick={openAdd} data-testid={`${config.apiPath}-add-btn`} className="gap-2">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {config.amountField && (
          <Card className="p-4" data-testid={`${config.apiPath}-total-card`}>
            <p className="text-xs tracking-[0.12em] uppercase font-semibold text-muted-foreground">
              {config.amountLabel}
              {monthFilter.length > 0 && (
                <span className="normal-case tracking-normal font-normal">
                  {" · "}
                  {monthFilter.length === 1 ? monthLabel(monthFilter[0]) : `${monthFilter.length} months`}
                </span>
              )}
            </p>
            <p className="mt-2 font-mono text-xl md:text-2xl font-light">{formatMoney(total, currency)}</p>
          </Card>
        )}
        <Card className="p-4">
          <p className="text-xs tracking-[0.12em] uppercase font-semibold text-muted-foreground">Records</p>
          <p className="mt-2 font-mono text-xl md:text-2xl font-light">{rows.length}</p>
        </Card>
        {statusCounts &&
          Object.entries(statusCounts)
            .slice(0, 2)
            .map(([k, v]) => (
              <Card className="p-4" key={k}>
                <p className="text-xs tracking-[0.12em] uppercase font-semibold text-muted-foreground">
                  {config.apiPath === "payables" && k === "Pending" ? "Payable" : k}
                </p>
                <p className="mt-2 font-mono text-xl md:text-2xl font-light">{v}</p>
              </Card>
            ))}
      </div>

      {/* Aging analysis */}
      {aging && (
        <div>
          <p className="text-[11px] tracking-[0.14em] uppercase font-semibold text-muted-foreground mb-2">
            Aging Analysis
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {aging.map((b, i) => {
              const tone = i === 0 ? "text-foreground" : i < 3 ? "text-warning" : "text-danger";
              return (
                <Card key={b.label} className="p-4" data-testid={`${config.apiPath}-aging-${i}`}>
                  <p className="text-[11px] tracking-[0.12em] uppercase font-semibold text-muted-foreground">
                    {b.label}
                  </p>
                  <p className={`mt-2 font-mono text-lg md:text-xl font-medium ${tone}`}>
                    {formatMoney(b.amount, currency)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {b.count} invoice{b.count === 1 ? "" : "s"}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Search & filters */}
      {rows.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center" data-testid={`${config.apiPath}-filters`}>
          <div className="relative flex-1 min-w-[240px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={config.searchPlaceholder || "Search vendor, description, invoice…"}
              className="pl-9"
              data-testid={`${config.apiPath}-search-input`}
            />
          </div>
          {accountOptions && accountOptions.opts.length > 0 && (
            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger className="w-full sm:w-52" data-testid={`${config.apiPath}-account-filter`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {accountOptions.opts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
                {accountOptions.hasUnassigned && <SelectItem value="__none__">No account</SelectItem>}
              </SelectContent>
            </Select>
          )}
          {monthOptions && monthOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 h-10 rounded-md bg-background border border-input px-3 text-sm w-full sm:w-56 justify-between hover:border-muted-foreground transition-colors"
                  data-testid={`${config.apiPath}-month-filter`}
                >
                  <span className="truncate">
                    {monthFilter.length === 0
                      ? "All months"
                      : monthFilter.length === 1
                      ? monthLabel(monthFilter[0])
                      : `${monthFilter.length} months selected`}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 overflow-auto w-56">
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); setMonthFilter([]); }}
                  data-testid={`${config.apiPath}-month-filter-all`}
                  className={monthFilter.length === 0 ? "text-brand" : ""}
                >
                  All months
                </DropdownMenuItem>
                {monthOptions.map((m) => {
                  const checked = monthFilter.includes(m);
                  return (
                    <DropdownMenuItem
                      key={m}
                      data-testid={`${config.apiPath}-month-filter-${m}`}
                      onSelect={(e) => {
                        e.preventDefault();
                        setMonthFilter((prev) =>
                          prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
                        );
                      }}
                      className={checked ? "text-brand" : ""}
                    >
                      <span className="mr-2 inline-block w-3 text-center">{checked ? "✓" : ""}</span>
                      {monthLabel(m)}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {config.monthField && (
            <div className="flex items-center gap-1.5" data-testid={`${config.apiPath}-date-range`}>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                title="From date"
                data-testid={`${config.apiPath}-date-from`}
                className="h-10 rounded-md bg-background border border-input px-2 text-xs w-[140px]"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                title="To date"
                data-testid={`${config.apiPath}-date-to`}
                className="h-10 rounded-md bg-background border border-input px-2 text-xs w-[140px]"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="text-xs text-muted-foreground hover:text-brand"
                  data-testid={`${config.apiPath}-date-range-clear`}
                  title="Clear range"
                >
                  ×
                </button>
              )}
            </div>
          )}
          {/* Category & Status filters are exposed as column-header dropdowns
              inside the table so this row stays roomy for search + date range. */}
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`${config.apiPath}-filter-count`}>
            Showing {filtered.length} of {rows.length}
          </span>
        </div>
      )}

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-md border border-brand/30 bg-brand/5 px-4 py-2.5" data-testid={`${config.apiPath}-bulk-bar`}>
          <span className="text-sm font-medium">{selected.size} selected</span>
          {bulkCatOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <Select value={bulkCat} onValueChange={setBulkCat}>
                <SelectTrigger className="w-48 h-8" data-testid={`${config.apiPath}-bulk-category`}>
                  <SelectValue placeholder="Set category to…" />
                </SelectTrigger>
                <SelectContent>
                  {bulkCatOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={bulkSetCategory} disabled={bulkBusy || !bulkCat} data-testid={`${config.apiPath}-bulk-apply-category`}>
                Apply
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2 sm:ml-auto">
            {moveTargets.length > 0 && (
              <Select value="" onValueChange={bulkMove} disabled={bulkBusy}>
                <SelectTrigger className="w-44 h-8" data-testid={`${config.apiPath}-bulk-move`}>
                  <SelectValue placeholder="Move to…" />
                </SelectTrigger>
                <SelectContent>
                  {moveTargets.map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} disabled={bulkBusy}>
              Clear
            </Button>
            <Button size="sm" variant="destructive" onClick={bulkDelete} disabled={bulkBusy} data-testid={`${config.apiPath}-bulk-delete`} className="gap-1.5">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Delete selected
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-brand" />
          </div>
        ) : display.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-muted-foreground">
            <Inbox className="h-8 w-8 mb-3" />
            <p className="text-sm">No records yet. Add one or import a file.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-20 flex flex-col items-center justify-center text-muted-foreground">
            <Search className="h-8 w-8 mb-3" />
            <p className="text-sm">No records match your search or filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allFilteredSelected}
                      onCheckedChange={toggleAll}
                      data-testid={`${config.apiPath}-select-all`}
                      aria-label="Select all"
                    />
                  </TableHead>
                  {config.columns.map((c) => {
                    const isStatusCol = config.statusField && c.key === config.statusField && !!statusCounts;
                    const isCategoryCol = c.key === "category" && categoryOptions && categoryOptions.length > 0;
                    let cellInner;
                    if (isStatusCol) {
                      cellInner = (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`${config.apiPath}-status-col-filter`}
                              className="inline-flex items-center gap-1 text-xs uppercase tracking-wider hover:text-brand transition-colors"
                            >
                              {c.label}
                              <ChevronDown className="h-3 w-3" />
                              {statusFilter !== "all" && (
                                <span className="text-[9px] normal-case tracking-normal text-brand2 border border-brand2/40 rounded px-1 ml-0.5">
                                  {statusFilter}
                                </span>
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-44">
                            <DropdownMenuItem
                              onClick={() => setStatusFilter("all")}
                              data-testid={`${config.apiPath}-status-col-all`}
                              className={statusFilter === "all" ? "text-brand" : ""}
                            >
                              All statuses
                            </DropdownMenuItem>
                            {Object.keys(statusCounts).filter((s) => s !== "—").map((s) => (
                              <DropdownMenuItem
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                data-testid={`${config.apiPath}-status-col-${s.toLowerCase()}`}
                                className={statusFilter === s ? "text-brand" : ""}
                              >
                                {config.apiPath === "payables" && s === "Pending" ? "Payable" : s}{" "}
                                <span className="ml-auto text-[10px] text-muted-foreground">{statusCounts[s] || 0}</span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    } else if (isCategoryCol) {
                      cellInner = (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`${config.apiPath}-category-col-filter`}
                              className="inline-flex items-center gap-1 text-xs uppercase tracking-wider hover:text-brand transition-colors"
                            >
                              {c.label}
                              <ChevronDown className="h-3 w-3" />
                              {catFilter !== "all" && (
                                <span className="text-[9px] normal-case tracking-normal text-brand2 border border-brand2/40 rounded px-1 ml-0.5">
                                  {catFilter}
                                </span>
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56 max-h-72 overflow-y-auto">
                            <DropdownMenuItem
                              onClick={() => setCatFilter("all")}
                              className={catFilter === "all" ? "text-brand" : ""}
                            >
                              All categories
                            </DropdownMenuItem>
                            {categoryOptions.map((cat) => (
                              <DropdownMenuItem
                                key={cat}
                                onClick={() => setCatFilter(cat)}
                                className={catFilter === cat ? "text-brand" : ""}
                              >
                                {cat}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    } else {
                      cellInner = c.label;
                    }
                    return (
                      <TableHead
                        key={c.key}
                        className={`text-xs uppercase tracking-wider ${c.type === "money" ? "text-right" : ""}`}
                      >
                        {cellInner}
                      </TableHead>
                    );
                  })}
                  <TableHead className="w-20 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((r, idx) => (
                  <TableRow
                    key={r.id}
                    onClick={config.rowDetail ? () => setDetailRow(r) : undefined}
                    className={`hover:bg-muted/60 ${config.rowDetail ? "cursor-pointer" : ""} ${selected.has(r.id) ? "bg-brand/5" : ""}`}
                    data-testid={`${config.apiPath}-row`}
                  >
                    <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggleOne(r.id)}
                        data-testid={`${config.apiPath}-row-select`}
                        aria-label="Select row"
                      />
                    </TableCell>
                    {config.columns.map((c) => (
                      <TableCell
                        key={c.key}
                        className={`${c.type === "money" ? "text-right" : ""} ${
                          c.key === config.columns[0].key ? "font-medium" : ""
                        }`}
                      >
                        {renderCell(r, c, safePage * PAGE_SIZE + idx)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {config.taggable && (
                          <button
                            onClick={() => setAllocRow(r)}
                            data-testid={`${config.apiPath}-tag-btn`}
                            title="Tag to projects / cost centres"
                            className="text-muted-foreground hover:text-brand transition-colors p-1"
                          >
                            <Tags className="h-4 w-4" />
                          </button>
                        )}
                        {config.apiPath === "payables" && String(r.status || "").toLowerCase() !== "paid" && (
                          <button
                            onClick={() => navigate(`/recon?status=unmatched&q=${encodeURIComponent(r.invoice_no || r.vendor || "")}`)}
                            data-testid={`payable-settle-${r.id}`}
                            title="Settle from Bank Reconciliation — upload the statement, then confirm this invoice"
                            className="text-muted-foreground hover:text-brand transition-colors p-1"
                          >
                            <GitCompareArrows className="h-4 w-4" />
                          </button>
                        )}
                        {moveTargets.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                data-testid={`${config.apiPath}-move-btn`}
                                className="text-muted-foreground hover:text-info transition-colors p-1"
                                title="Move to…"
                              >
                                <ArrowRightLeft className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {moveTargets.map(([k, label]) => (
                                <DropdownMenuItem key={k} onClick={() => moveOne(r.id, k)} data-testid={`${config.apiPath}-move-to-${k}`}>
                                  Move to {label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <button
                          onClick={() => openEdit(r)}
                          data-testid={`${config.apiPath}-edit-btn`}
                          className="text-muted-foreground hover:text-brand transition-colors p-1"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => remove(r.id)}
                          data-testid={`${config.apiPath}-delete-btn`}
                          className="text-muted-foreground hover:text-danger transition-colors p-1"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {!loading && pageCount > 1 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border text-xs" data-testid={`${config.apiPath}-pagination`}>
            <span className="text-muted-foreground">
              Rows {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-7 px-3" disabled={safePage === 0}
                onClick={() => setPageIdx(safePage - 1)} data-testid={`${config.apiPath}-prev-page`}>
                Prev
              </Button>
              <span className="font-mono text-muted-foreground">{safePage + 1} / {pageCount}</span>
              <Button size="sm" variant="outline" className="h-7 px-3" disabled={safePage >= pageCount - 1}
                onClick={() => setPageIdx(safePage + 1)} data-testid={`${config.apiPath}-next-page`}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add / Edit Dialog */}
      <EntityForm
        config={config}
        open={addOpen}
        onOpenChange={setAddOpen}
        editingRow={editingRow}
        accounts={accounts}
        onSaved={load}
      />

      {config.taggable && (
        <AllocationDialog
          row={allocRow}
          apiPath={config.apiPath}
          open={!!allocRow}
          onOpenChange={(o) => !o && setAllocRow(null)}
          onSaved={load}
        />
      )}

      {/* Import Dialog */}
      <EntityImporter
        config={config}
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={load}
      />

      {/* Row Detail Drawer */}
      {config.rowDetail && (
        <EntityDetailDrawer
          config={config}
          row={detailRow}
          open={!!detailRow}
          onOpenChange={(o) => !o && setDetailRow(null)}
          onEdit={() => {
            const r = detailRow;
            setDetailRow(null);
            openEdit(r);
          }}
        />
      )}
    </div>
  );
}
